import { useEffect, useState } from 'react';
import { fetchPlaceDetails, placePhotoUrl } from '../lib/api.js';

/**
 * Process-wide cache of Places lookups, keyed by restaurant id.
 *
 * Deliberately module-level rather than component state: the swipe deck mounts
 * and unmounts the same restaurant repeatedly as cards cycle, and each lookup
 * costs a billable API call.
 *
 * @type {Map<number, import('../lib/types.js').PlaceDetails | null>}
 */
const cache = new Map();

/**
 * Loads live Google Places enrichment for one restaurant.
 *
 * Resolves to `details: null` when the integration is disabled or the venue
 * isn't found, which callers treat as "render the bundled placeholder content".
 *
 * @param {import('../lib/types.js').Restaurant} restaurant
 * @param {object} [options]
 * @param {boolean} [options.enabled]  Skip the request entirely when false.
 * @returns {{details: import('../lib/types.js').PlaceDetails | null, loading: boolean, photoUrls: string[], photoAttributions: {name: string|null, uri: string|null, sourceUri: string|null}[]}}
 */
export function usePlaceDetails(restaurant, { enabled = true } = {}) {
  const cached = cache.get(restaurant.id);

  const [details, setDetails] = useState(cached ?? null);
  const [loading, setLoading] = useState(enabled && cached === undefined);

  useEffect(() => {
    if (!enabled) return;

    if (cache.has(restaurant.id)) {
      setDetails(cache.get(restaurant.id) ?? null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    fetchPlaceDetails(restaurant.name, restaurant.id, restaurant.googlePlaceId)
      .then((result) => {
        cache.set(restaurant.id, result);
        if (!active) return;
        setDetails(result);
      })
      .catch(() => {
        cache.set(restaurant.id, null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [enabled, restaurant.googlePlaceId, restaurant.id, restaurant.name]);

  const photoUrls = (details?.photos ?? []).map((photo) => placePhotoUrl(photo.ref));
  // Google requires each photo's author be credited wherever the photo shows.
  const photoAttributions = (details?.photos ?? []).map((photo) => ({
    name: photo.authorName ?? null,
    uri: photo.authorUri ?? null,
    sourceUri: photo.sourceUri ?? photo.authorUri ?? details?.googleMapsUri ?? null,
  }));

  return { details, loading, photoUrls, photoAttributions };
}
